import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { color, motion as motionTokens, radius, space } from "@tokens/index";
import { Text } from "@primitives/Text";

/**
 * Realtime connection indicator: an 8px status dot plus a label.
 *
 * Pure presentation. The caller passes the connection state; this component
 * owns no subscription. Color never carries the meaning alone: the label is
 * always in the DOM. With `compact` the label is visually hidden and the
 * dot stands alone, as the charter requires below the `expanded` class.
 *
 * Nothing is shown until the first `connected`. A session starts as
 * `disconnected` while the hub negotiates; painting "Offline" and then
 * "Live" a moment later is the flicker the user notices. The indicator
 * keeps its footprint (so the bar does not shift) and fades in once the
 * connection is real. After that, every state shows.
 *
 * "Reconnecting…" is shown only when that state persists. A transport blip
 * that heals inside `reconnectingDelayMs` never reaches the header. The
 * connection state itself is not delayed; only its display is.
 */

export type RealtimeIndicatorState = "connected" | "reconnecting" | "disconnected";

export const RECONNECTING_DISPLAY_DELAY_MS = 1500;

export interface RealtimeStatusIndicatorProps {
    readonly state: RealtimeIndicatorState;
    /** Dot only. The label stays in the accessibility tree. */
    readonly compact?: boolean;
    /** How long `reconnecting` must persist before it is shown. */
    readonly reconnectingDelayMs?: number;
}

function useSettledState(state: RealtimeIndicatorState, delayMs: number): RealtimeIndicatorState {
    const [shown, setShown] = useState(state);
    useEffect(() => {
        if (state !== "reconnecting") {
            setShown(state);
            return;
        }
        const timer = setTimeout(() => {
            setShown("reconnecting");
        }, delayMs);
        return () => {
            clearTimeout(timer);
        };
    }, [state, delayMs]);
    return shown;
}

/** `true` from the first `connected` onward. */
function useHasConnected(state: RealtimeIndicatorState): boolean {
    const [seen, setSeen] = useState(state === "connected");
    useEffect(() => {
        if (state === "connected") {
            setSeen(true);
        }
    }, [state]);
    return seen;
}

type StatusTone = "success" | "warning" | "danger";

interface StateAppearance {
    readonly label: string;
    readonly tone: StatusTone;
}

const toneDot: Record<StatusTone, string> = {
    success: color.status.successFg,
    warning: color.status.warningFg,
    danger: color.status.dangerFg,
};

function appearanceFor(state: RealtimeIndicatorState): StateAppearance {
    switch (state) {
        case "connected":
            return { label: "Live", tone: "success" };
        case "reconnecting":
            return { label: "Reconnecting…", tone: "warning" };
        case "disconnected":
            return { label: "Offline", tone: "danger" };
    }
}

const rootStyle: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: space[2],
    minHeight: 24,
    paddingInline: space[1],
    lineHeight: 1,
    userSelect: "none",
};

function dotStyle(tone: StatusTone): CSSProperties {
    return {
        width: 8,
        height: 8,
        borderRadius: radius.full,
        background: toneDot[tone],
        flexShrink: 0,
    };
}

export function RealtimeStatusIndicator({
    state,
    compact = false,
    reconnectingDelayMs = RECONNECTING_DISPLAY_DELAY_MS,
}: RealtimeStatusIndicatorProps): ReactElement {
    const shownState = useSettledState(state, reconnectingDelayMs);
    const appearance = appearanceFor(shownState);
    const revealed = useHasConnected(state);

    return (
        <span
            role="status"
            aria-live="polite"
            aria-label={`Realtime connection: ${appearance.label}`}
            aria-hidden={revealed ? undefined : true}
            title={compact ? appearance.label : undefined}
            data-realtime-state={shownState}
            data-realtime-revealed={revealed ? "" : undefined}
            data-tone={appearance.tone}
            style={{
                ...rootStyle,
                visibility: revealed ? "visible" : "hidden",
                opacity: revealed ? 1 : 0,
                transition: `opacity ${motionTokens.control.toggle} ${motionTokens.easings.standard}`,
            }}
        >
            <span aria-hidden="true" style={dotStyle(appearance.tone)} />
            {compact ? (
                <span data-visually-hidden="">{appearance.label}</span>
            ) : (
                <Text role="caption" tone="muted" weight="medium">
                    {appearance.label}
                </Text>
            )}
        </span>
    );
}
